import React, { useEffect, useState } from "react";
import { Footer, Navbar } from "../components";
import { useParams, Link } from "react-router-dom";
import api from "../utils/base_url";

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await api.get(`orderdetails/${orderId}/`);
        setOrder(res.data.data);
      } catch (err) {
        console.error("Failed to fetch order", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <h4 style={{ color: "#4A1F1A" }}>Loading order details...</h4>
      </div>
    );
  }



  const formatStatus = (value) => {
    if (!value) return "";
    return value
      .replace(/_/g, " ")            // replace underscore with space
      .replace(/\b\w/g, char => char.toUpperCase()); // capitalize each word
  };
  const statusColors = {
    pending: "bg-warning text-dark",
    order_confirmed: "bg-info text-white",
    shipped: "bg-primary text-white",
    delivered: "bg-success",
    cancelled: "bg-danger",
    returned: "bg-secondary text-white"
  };

  if (!order) {
    return (
      <div className="container py-5 text-center">
        <h4 style={{ color: "#4A1F1A" }}>Order not found!</h4>
        <Link to="/" className="btn btn-outline-themed mt-3">
          Go to Home
        </Link>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-10">
            <div className="card rounded-theme shadow-theme">
              <div className="card-header text-white text-center rounded-top-theme" style={{ backgroundColor: "#4A1F1A", color: "#fff" }}>

                <h4>Order Confirmed!</h4>
              </div>
              <div className="card-body">
                <h5 className="card-title mb-3 text-center text-theme-dark">
                  Thank you, <strong>{order.customer_name}</strong>
                </h5>

                <p className="text-center text-theme-medium">
                  Your order <strong>#{order.order_number}</strong> has been placed successfully.
                </p>

                <div className="row mb-4 text-theme-dark">
                  <div className="col-12 col-md-6 mb-3 mb-md-0">
                    <p><strong>Status: </strong><span className={`badge ${statusColors[order.status] || "bg-dark text-white"}`}>
                      {formatStatus(order.status)}
                    </span>
                    </p>
                    <p><strong>Payment Method:</strong> {order.payment_method === "cod" ? "Cash on Delivery" : "Online Payment"}</p>
                    <p><strong>Payment Status:</strong> {order.payment_status}</p>
                  </div>
                  <div className="col-12 col-md-6">
                    <p><strong>Shipping Address:</strong> {order.shipping_address}</p>
                    <p><strong>Billing Address:</strong> {order.billing_address}</p>
                    <p><strong>Ordered At:</strong> {new Date(order.ordered_at).toLocaleString()}</p>
                  </div>
                </div>

                <h5 className="mb-3 text-theme-dark">Order Items</h5>
                <div className="table-responsive">
                  <table className="table table-bordered rounded-theme mb-0">
                    <thead className="table-light text-theme-dark">
                      <tr>
                        <th>Product</th>
                        <th>Varient</th>
                        <th>Quantity</th>
                        <th>Price</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.product_name}</td>
                          <td>
                            {["size", "color"].includes(
                              item.quantity_unit?.toLowerCase()
                            )
                              ? item.quantity_value
                              : `${Number(item.quantity_value)} ${item.quantity_unit}`}
                          </td>
                          <td>{item.quantity}</td>
                          <td>₹{item.price.toLocaleString()}</td>
                          <td>₹{item.total.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="text-end mt-4 text-theme-dark">
                  {(() => {
                    const rawSubtotal = order.subtotal_before_discount != null
                      ? Number(order.subtotal_before_discount)
                      : (order.items?.reduce((acc, item) => acc + (Number(item.total) || (Number(item.price) * (item.quantity || 1))), 0) || (Number(order.subtotal || 0) + Number(order.tax || 0)));
                    const discountAmount = Number(order.discount_amount || 0);
                    const discountedSubtotal = Math.max(0, rawSubtotal - discountAmount);
                    const shippingCost = Number(order.shipping_cost || 0);
                    const totalAmount = Number(order.total_amount || 0);

                    return (
                      <div className="d-inline-block text-end" style={{ minWidth: "260px" }}>
                        <div className="d-flex justify-content-between mb-1">
                          <span className="me-3"><strong>Subtotal:</strong></span>
                          <span>₹{rawSubtotal.toLocaleString()}</span>
                        </div>

                        {discountAmount > 0 && (
                          <div className="d-flex justify-content-between text-success mb-1 fw-semibold">
                            <span className="me-3">
                              🏷️ Promo {order.coupon_code ? `(${order.coupon_code})` : "Discount"}:
                            </span>
                            <span>-₹{discountAmount.toLocaleString()}</span>
                          </div>
                        )}

                        {discountAmount > 0 && (
                          <div className="d-flex justify-content-between text-secondary mb-1 small">
                            <span className="me-3">Discounted Subtotal:</span>
                            <span>₹{discountedSubtotal.toLocaleString()}</span>
                          </div>
                        )}

                        <div className="d-flex justify-content-between mb-2">
                          <span className="me-3"><strong>Shipping:</strong></span>
                          <span>₹{shippingCost.toLocaleString()}</span>
                        </div>

                        <hr className="my-2" />

                        <div className="d-flex justify-content-between">
                          <h5 className="mb-0"><strong>Total Amount:</strong></h5>
                          <h5 className="mb-0"><strong>₹{totalAmount.toLocaleString()}</strong></h5>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div className="text-center mt-4">
                  <a href="/" className="btn-themed">
                    Continue Shopping
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />

      <style>{`
        :root {
          --brown-dark: #4A1F1A;
          --brown-darker:  #8A4B27;
          --brown-light: #E9DCC3;
          --cream-bg: #E9DCC3;
          --text-dark: #000000ff;
          --text-medium: #000000ff;
          --text-muted:  rgb(112,168,77);
        }

        .btn-themed {
  text-decoration: none !important;
}


        body, html, .container {
          background-color: var(--cream-bg);
        }

        .rounded-theme {
          border-radius: 12px !important;
        }
        .rounded-top-theme {
          border-top-left-radius: 12px !important;
          border-top-right-radius: 12px !important;
        }
        .shadow-theme {
          box-shadow: 0 5px 14px rgba(122, 86, 58, 0.15) !important;
        }

        .text-theme-dark {
          color: var(--text-dark);
        }

        .text-theme-medium {
          color: var(--text-medium);
        }

.btn-themed {
  background-color: var(--brown-dark);
  color: #fff !important;
  border-radius: 8px;
  font-weight: 600;
  padding: 10px 18px;
  border: none;

  display: inline-flex;      /* 🔥 inline instead of full block */
  align-items: center;
  justify-content: center;

  text-align: center;
  cursor: pointer;

  width: auto;               /* 🔥 remove fixed width */
  min-width: 180px;          /* ✅ small but readable */
}


        .btn-themed:hover,
        .btn-themed:focus {
          background-color: var(--brown-darker);
          outline: none;
          text-decoration: none;
          box-shadow: 0 0 10px rgba(122, 86, 58, 0.6);
        }

        .table-bordered {
          border: 1px solid var(--brown-light);
        }

        .table-bordered th,
        .table-bordered td {
          border: 1px solid var(--brown-light);
          vertical-align: middle;
        }

        .table-light {
          background-color: var(--cream-bg);
          color: var(--text-dark);
        }

        /* Responsive improvements */
        @media (max-width: 767px) {
          .col-md-6 {
            margin-bottom: 1rem;
          }
          .table-responsive {
            margin-bottom: 1rem;
          }
          .card-header h4 {
            font-size: 1.5rem;
          }
          .btn-themed {
            font-size: 1.1rem;
            padding: 12px 20px;
          }
        }
      `}</style>
    </>
  );
};

export default OrderConfirmation;
